import { useState } from "react";
import { Box, Typography, Stack } from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

import { Section, Container, SectionHeader, COLORS } from "../shared/ui";

const FAQ_ITEMS = [
  {
    q: "Что если после замера я найду ошибку?",
    a: "Мы проверяем каждый чертёж перед выдачей. Если всё же обнаружится несоответствие — бесплатно выезжаем на повторный замер и перерисовываем чертёж. Гарантия точности ±2 мм зафиксирована в договоре.",
  },
  {
    q: "Могу ли я присутствовать на замере?",
    a: "Конечно. Но ваше присутствие не обязательно — мы привыкли работать автономно. Достаточно передать ключи (можем забрать и вернуть сами за 3 000 ₽) или встретить замерщика на объекте.",
  },
  {
    q: "Работаете ли по договору?",
    a: "Да, работаем с юрлицами и ИП. Договор, счёт, акт, счёт-фактура — все документы предоставляем.",
  },
  {
    q: "Сколько стоит замер в моём городе?",
    a: "Базовая ставка 150 ₽/м² действует во всех 150+ городах, где мы работаем. Минимальная стоимость выезда — 7 000 ₽. В черте города выезд бесплатный, за город — 50 ₽/км от центра. Точную цену покажет калькулятор выше.",
  },
  {
    q: "В какой программе вы отрисовываете чертёж?",
    a: "В любой, которая удобна вам: AutoCAD (.dwg), ArchiCAD (.pln / .pla), Revit (.rvt), nanoCAD, FreeCAD. Плюс всегда прикладываем PDF. Программу вы выбираете сами при оформлении заявки.",
  },
  {
    q: "Как быстро получу результат?",
    a: "Стандартный срок — 2–3 рабочих дня с момента выезда. Для объектов больше 300 м² — до 4 дней, больше 800 м² — до 5 дней. Есть услуга «Срочный выезд» (+3 000 ₽) — замер и чертёж за 1 сутки.",
  },
  {
    q: "Что если объект без отопления зимой?",
    a: "Работаем круглый год, но зимой на неотапливаемых объектах применяется повышающий коэффициент +20% (оборудование требует больше времени на стабилизацию, а замерщики — тёплых перерывов).",
  },
  {
    q: "Замеряете ли электрику и сантехнику?",
    a: "Да. Сантехника (привязки канализации, водоснабжения, приборов) входит в базовую стоимость. Электрика (розетки, выключатели, щиток, автоматы) — дополнительная услуга за +25 ₽/м².",
  },
  {
    q: "Можете ли выехать за пределы города?",
    a: "Да, выезжаем в радиусе 300+ км от центра любого из 150+ городов покрытия. Стоимость выезда — 50 ₽ за каждый км от центра города до объекта. На большие расстояния обсуждаем индивидуально.",
  },
  {
    q: "Как проходит оплата?",
    a: "Для физлиц — карта, СБП или наличные в день выезда. Для юрлиц — счёт и безналичный расчёт, возможна работа по постоплате. Сроки закреплены в договоре.",
  },
  {
    q: "Делаете ли 3D-сканирование?",
    a: "Да, для сложных объектов — лестницы, многоуровневые пространства, исторические помещения, большие торговые площади. Обсуждается индивидуально, стоимость зависит от объёма. Оставьте заявку — рассчитаем.",
  },
  {
    q: "А что вы делаете с коммерческой тайной клиента?",
    a: "Подписываем NDA, если требуется. Все файлы хранятся на защищённом Яндекс.Диске с доступом только для вашей команды. Замерщики не публикуют фото объектов без разрешения.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <Section id="faq" py={{ xs: 10, md: 14 }} sx={{ background: "#fff" }}>
      <Container maxWidth={860}>
        <SectionHeader
          eyebrow="Частые вопросы"
          title="Отвечаем на всё"
          subtitle="Если вашего вопроса нет в списке — напишите менеджеру в чат, ответит за пару минут"
        />

        <Stack spacing={1.5}>
          {FAQ_ITEMS.map((item, i) => (
            <FAQItem
              key={item.q}
              item={item}
              index={i}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
            />
          ))}
        </Stack>
      </Container>
    </Section>
  );
}

function FAQItem({ item, index, isOpen, onToggle }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.3) }}
    >
      <Box
        onClick={onToggle}
        sx={{
          cursor: "pointer",
          borderRadius: "16px",
          border: `2px solid ${isOpen ? "rgba(99,102,241,0.35)" : "rgba(99,102,241,0.1)"}`,
          background: isOpen ? "rgba(99,102,241,0.03)" : "#fff",
          transition: "all 0.25s ease",
          overflow: "hidden",
          "&:hover": { borderColor: "rgba(99,102,241,0.25)" },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ p: { xs: 2.25, md: 2.75 } }}
        >
          <Typography
            level="title-md"
            sx={{
              fontWeight: 700,
              color: COLORS.dark,
              lineHeight: 1.4,
              fontSize: { xs: "0.95rem", md: "1.05rem" },
            }}
          >
            {item.q}
          </Typography>
          <Box
            sx={{
              flexShrink: 0,
              width: 32,
              height: 32,
              borderRadius: "10px",
              background: isOpen ? COLORS.brand : "rgba(99,102,241,0.1)",
              color: isOpen ? "#fff" : COLORS.brand,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.25s ease",
            }}
          >
            <motion.div animate={{ rotate: isOpen ? 45 : 0 }} transition={{ duration: 0.25 }}>
              <AddRoundedIcon sx={{ fontSize: 22, color: isOpen ? "#fff" : COLORS.brand, }} />
            </motion.div>
          </Box>
        </Stack>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              style={{ overflow: "hidden" }}
            >
              <Box sx={{ px: { xs: 2.25, md: 2.75 }, pb: { xs: 2.25, md: 2.75 }, pt: 0 }}>
                <Box
                  sx={{
                    borderTop: "1px dashed rgba(99,102,241,0.2)",
                    pt: 2,
                  }}
                >
                  <Typography
                    level="body-md"
                    sx={{ color: "neutral.600", lineHeight: 1.65 }}
                  >
                    {item.a}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </motion.div>
  );
}